import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Breadcrumb, Button, Dropdown, Modal, Spinner } from "react-bootstrap";
import { ChevronLeft, ChevronRight, Plus, Trash, X } from "react-bootstrap-icons";
import DatePicker from "react-datepicker";
import moment from "moment";
import {
  AuthService,
  CustomerService,
  CustomerAbsenseService,
} from "../../services";
import {
  isCustomerListActive,
  WEEKDAY_KEYS,
  normalizeWeekdayList,
  getCustomerDefaultAttendanceDays,
  getCustomerDayStatus,
  isScheduleLeaveAbsence,
  indexAbsencesByCustomerId,
  parseCustomerScheduleDate,
} from "../../shared";
import "react-datepicker/dist/react-datepicker.css";

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const WEEKDAY_FULL_LABELS = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

const getWeekStart = (date) => moment(date).startOf("isoWeek");

const formatCustomerDisplayName = (customer) => {
  const name = `${customer?.name || ""}`.trim();
  const nameCn = `${customer?.name_cn || ""}`.trim();
  if (!name) return nameCn || "—";
  return nameCn ? `${name} (${nameCn})` : name;
};

const formatAbsenseDateLabel = (value) => {
  const m = parseCustomerScheduleDate(value);
  return m ? m.format("MMM D, YYYY") : `${value || ""}`;
};

const formatWeekLeaveRange = (item) => {
  const start = parseCustomerScheduleDate(item?.start_date);
  const end = parseCustomerScheduleDate(item?.end_date);
  const startText = start ? start.format("YYYY-MM-DD") : `${item?.start_date || ""}`;
  const endText = end ? end.format("YYYY-MM-DD") : `${item?.end_date || ""}`;
  const note = `${item?.note || ""}`.trim();
  return note ? `${startText} to ${endText} — ${note}` : `${startText} to ${endText}`;
};

const absenceCoversWeek = (item, weekStart, weekEnd) => {
  const start = parseCustomerScheduleDate(item?.start_date);
  const end = parseCustomerScheduleDate(item?.end_date);
  if (!start || !end) return false;
  return start.isSameOrBefore(weekEnd, "day") && end.isSameOrAfter(weekStart, "day");
};

const splitAbsensesByTiming = (records = []) => {
  const today = moment().startOf("day");
  const upcoming = [];
  const past = [];
  records
    .filter((item) => isScheduleLeaveAbsence(item))
    .forEach((item) => {
      const end = parseCustomerScheduleDate(item?.end_date);
      if (end && end.isBefore(today, "day")) {
        past.push(item);
      } else {
        upcoming.push(item);
      }
    });
  upcoming.sort((a, b) => `${a.start_date || ""}`.localeCompare(`${b.start_date || ""}`));
  past.sort((a, b) => `${b.end_date || ""}`.localeCompare(`${a.end_date || ""}`));
  return { upcoming, past };
};

const getCurrentUserName = () => {
  try {
    const user = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : null;
    return `${user?.name || user?.username || ""}`.trim();
  } catch (_err) {
    return "";
  }
};

const CustomerSchedule = () => {
  const navigate = useNavigate();
  const canView = AuthService.canViewCustomerSchedule();
  const canEdit = AuthService.canEditCustomerSchedule();
  const [customers, setCustomers] = useState([]);
  const [absencesByCustomerId, setAbsencesByCustomerId] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [weekAnchor, setWeekAnchor] = useState(() => getWeekStart(new Date()).toDate());
  const [daySavingKey, setDaySavingKey] = useState("");

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [selectedDays, setSelectedDays] = useState([]);
  const [upcomingLeave, setUpcomingLeave] = useState([]);
  const [pastLeave, setPastLeave] = useState([]);
  const [showPastLeave, setShowPastLeave] = useState(false);
  const [saving, setSaving] = useState(false);

  const [showAddLeaveModal, setShowAddLeaveModal] = useState(false);
  const [leaveStartDate, setLeaveStartDate] = useState(null);
  const [leaveEndDate, setLeaveEndDate] = useState(null);
  const [leaveNote, setLeaveNote] = useState("");
  const [savingLeave, setSavingLeave] = useState(false);
  const [deletingLeaveId, setDeletingLeaveId] = useState("");

  const weekDays = useMemo(() => {
    const start = getWeekStart(weekAnchor);
    return WEEKDAY_KEYS.map((key, index) => {
      const day = start.clone().add(index, "days");
      return {
        key,
        label: WEEKDAY_LABELS[index],
        date: day.toDate(),
        dateKey: day.format("YYYY-MM-DD"),
        dateLabel: day.format("MM-DD"),
        moment: day,
      };
    });
  }, [weekAnchor]);

  const weekRangeLabel = useMemo(() => {
    const start = getWeekStart(weekAnchor);
    const end = start.clone().add(6, "days");
    return `${start.format("MMM D, YYYY")} – ${end.format("MMM D, YYYY")}`;
  }, [weekAnchor]);

  const weekBounds = useMemo(() => {
    const start = getWeekStart(weekAnchor);
    return { start, end: start.clone().add(6, "days") };
  }, [weekAnchor]);

  const filteredCustomers = useMemo(() => {
    const keyword = `${searchText || ""}`.trim().toLowerCase();
    if (!keyword) return customers;
    return customers.filter((customer) => {
      const name = `${customer?.name || ""}`.toLowerCase();
      const nameCn = `${customer?.name_cn || ""}`.toLowerCase();
      const display = formatCustomerDisplayName(customer).toLowerCase();
      return name.includes(keyword) || nameCn.includes(keyword) || display.includes(keyword);
    });
  }, [customers, searchText]);

  const loadCustomers = () => {
    return CustomerService.getAllActiveCustomers()
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        const active = list
          .filter((customer) => isCustomerListActive(customer))
          .sort((a, b) =>
            formatCustomerDisplayName(a).localeCompare(formatCustomerDisplayName(b), undefined, {
              sensitivity: "base",
            })
          );
        setCustomers(active);
        return active;
      })
      .catch(() => {
        setCustomers([]);
        return [];
      });
  };

  const loadAllAbsences = () => {
    return CustomerAbsenseService.getAll()
      .then((res) => {
        const map = indexAbsencesByCustomerId(res?.data || []);
        setAbsencesByCustomerId(map);
        return map;
      })
      .catch(() => {
        setAbsencesByCustomerId({});
        return {};
      });
  };

  const loadCustomerAbsenses = (customerId) => {
    if (!customerId) {
      setUpcomingLeave([]);
      setPastLeave([]);
      return Promise.resolve();
    }
    return CustomerAbsenseService.getAll(customerId)
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        const { upcoming, past } = splitAbsensesByTiming(list);
        setUpcomingLeave(upcoming);
        setPastLeave(past);
      })
      .catch(() => {
        setUpcomingLeave([]);
        setPastLeave([]);
      });
  };

  useEffect(() => {
    if (!canView) {
      window.alert(
        "You haven't login yet OR this user does not have access to this page. Please change an admin account to login."
      );
      AuthService.logout();
      navigate("/login");
      return;
    }

    setLoading(true);
    Promise.all([loadCustomers(), loadAllAbsences()]).finally(() => setLoading(false));
  }, [navigate, canView]);

  const shiftWeek = (delta) => {
    setWeekAnchor((prev) => getWeekStart(prev).add(delta, "weeks").toDate());
  };

  const onPickDate = (date) => {
    if (!date) return;
    setWeekAnchor(getWeekStart(date).toDate());
  };

  const getDayStatus = (customer, day) =>
    getCustomerDayStatus(customer, day.moment, { absencesByCustomerId });

  const getWeekLeaveItems = (customer) => {
    const absences = (absencesByCustomerId[`${customer?.id || ""}`] || []).filter(
      isScheduleLeaveAbsence
    );
    return absences.filter((item) =>
      absenceCoversWeek(item, weekBounds.start, weekBounds.end)
    );
  };

  const applyDayStatusChange = async (customer, day, nextStatus) => {
    if (!canEdit || !customer?.id) return;
    const current = getDayStatus(customer, day);
    if (current.locked || current.status === nextStatus) return;

    const saveKey = `${customer.id}:${day.dateKey}`;
    setDaySavingKey(saveKey);
    try {
      await CustomerAbsenseService.clearDayOverrides(customer.id, day.dateKey);
      await CustomerAbsenseService.create({
        customer_id: customer.id,
        name: formatCustomerDisplayName(customer),
        start_date: day.dateKey,
        end_date: day.dateKey,
        note: "",
        type: nextStatus === "coming" ? "attend" : "leave",
        source: "day_override",
        create_by: getCurrentUserName(),
      });
      await loadAllAbsences();
    } catch (error) {
      console.error("Failed to update day status:", error);
      window.alert(error?.response?.data?.message || "Failed to update day status.");
    } finally {
      setDaySavingKey("");
    }
  };

  const openEditModal = (customer) => {
    setEditingCustomer(customer);
    setSelectedDays(getCustomerDefaultAttendanceDays(customer));
    setUpcomingLeave([]);
    setPastLeave([]);
    setShowPastLeave(false);
    setShowEditModal(true);
    loadCustomerAbsenses(customer?.id);
  };

  const closeEditModal = () => {
    if (saving || savingLeave || deletingLeaveId) return;
    setShowEditModal(false);
    setEditingCustomer(null);
    setShowAddLeaveModal(false);
  };

  const toggleDefaultDay = (dayKey) => {
    if (!canEdit) return;
    setSelectedDays((prev) => {
      if (prev.includes(dayKey)) {
        return prev.filter((d) => d !== dayKey);
      }
      return normalizeWeekdayList([...prev, dayKey]);
    });
  };

  const openAddLeaveModal = () => {
    if (!canEdit || !editingCustomer) return;
    setLeaveStartDate(null);
    setLeaveEndDate(null);
    setLeaveNote("");
    setShowAddLeaveModal(true);
  };

  const closeAddLeaveModal = () => {
    if (savingLeave) return;
    setShowAddLeaveModal(false);
  };

  const saveLeave = async () => {
    if (!editingCustomer || !canEdit || savingLeave) return;
    if (!leaveStartDate || !leaveEndDate) {
      window.alert("Please select start date and end date.");
      return;
    }
    const start = moment(leaveStartDate).startOf("day");
    const end = moment(leaveEndDate).startOf("day");
    if (end.isBefore(start, "day")) {
      window.alert("End date must be on or after start date.");
      return;
    }

    setSavingLeave(true);
    try {
      await CustomerAbsenseService.create({
        customer_id: editingCustomer.id,
        name: formatCustomerDisplayName(editingCustomer),
        start_date: start.format("YYYY-MM-DD"),
        end_date: end.format("YYYY-MM-DD"),
        note: `${leaveNote || ""}`.trim(),
        type: "leave",
        source: "schedule_leave",
        create_by: getCurrentUserName(),
      });
      await Promise.all([
        loadCustomerAbsenses(editingCustomer.id),
        loadAllAbsences(),
      ]);
      setShowAddLeaveModal(false);
      setLeaveStartDate(null);
      setLeaveEndDate(null);
      setLeaveNote("");
    } catch (error) {
      console.error("Failed to save customer absense:", error);
      window.alert(error?.response?.data?.message || "Failed to save leave.");
    } finally {
      setSavingLeave(false);
    }
  };

  const deleteLeave = async (leaveItem) => {
    if (!canEdit || !leaveItem?.id || deletingLeaveId || savingLeave) return;
    const dateLabel = `${formatAbsenseDateLabel(leaveItem.start_date)} – ${formatAbsenseDateLabel(leaveItem.end_date)}`;
    if (!window.confirm(`Delete leave ${dateLabel}?`)) return;

    setDeletingLeaveId(leaveItem.id);
    try {
      await CustomerAbsenseService.remove(leaveItem.id);
      await Promise.all([
        loadCustomerAbsenses(editingCustomer?.id),
        loadAllAbsences(),
      ]);
    } catch (error) {
      console.error("Failed to delete customer absense:", error);
      window.alert(error?.response?.data?.message || "Failed to delete leave.");
    } finally {
      setDeletingLeaveId("");
    }
  };

  const saveDefaultSchedule = async () => {
    if (!editingCustomer || !canEdit || saving) return;
    setSaving(true);
    try {
      await CustomerService.updateCustomer(editingCustomer.id, {
        days_of_week: selectedDays,
      });
      await loadCustomers();
      setShowEditModal(false);
      setEditingCustomer(null);
    } catch (error) {
      console.error("Failed to save customer days_of_week:", error);
      window.alert(error?.response?.data?.message || "Failed to save schedule.");
    } finally {
      setSaving(false);
    }
  };

  const renderLeaveList = (items, emptyText, { allowDelete = false } = {}) => {
    if (!items?.length) {
      return <div className="customer-schedule-leave-empty">{emptyText}</div>;
    }
    return (
      <div className="customer-schedule-leave-list">
        {items.map((item) => {
          const itemKey = item.id || `${item.start_date}-${item.end_date}-${item.note}`;
          const isDeleting = deletingLeaveId === item.id;
          return (
            <div key={itemKey} className="customer-schedule-leave-item">
              <div className="customer-schedule-leave-item-main">
                <div className="customer-schedule-leave-item-dates">
                  {formatAbsenseDateLabel(item.start_date)}
                  {" – "}
                  {formatAbsenseDateLabel(item.end_date)}
                </div>
                {`${item.note || ""}`.trim() !== "" && (
                  <div className="customer-schedule-leave-item-note">{item.note}</div>
                )}
              </div>
              {allowDelete && canEdit && item.id && (
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm customer-schedule-leave-delete-btn"
                  onClick={() => deleteLeave(item)}
                  disabled={!!deletingLeaveId || savingLeave || saving}
                  title="Delete leave"
                  aria-label="Delete leave"
                >
                  {isDeleting ? "…" : <Trash size={14} />}
                </button>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderDayStatusCell = (customer, day) => {
    const { status, locked } = getDayStatus(customer, day);
    const savingThis = daySavingKey === `${customer.id}:${day.dateKey}`;
    const label = status === "coming" ? "Coming" : "Leave";
    const pillClass =
      status === "coming"
        ? "customer-schedule-status-pill is-coming"
        : "customer-schedule-status-pill is-leave";

    if (locked || !canEdit) {
      return (
        <span className={`${pillClass}${locked ? " is-locked" : ""}`}>{label}</span>
      );
    }

    return (
      <Dropdown
        onSelect={(eventKey) => {
          if (!eventKey || savingThis) return;
          applyDayStatusChange(customer, day, eventKey);
        }}
      >
        <Dropdown.Toggle
          as="button"
          type="button"
          className={`${pillClass} has-toggle`}
          disabled={savingThis}
        >
          {savingThis ? "…" : label}
        </Dropdown.Toggle>
        <Dropdown.Menu align="start">
          <Dropdown.Item eventKey="coming" active={status === "coming"}>
            Coming
          </Dropdown.Item>
          <Dropdown.Item eventKey="leave" active={status === "leave"}>
            Leave
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  return (
    <>
      <div className="list row mb-4">
        <Breadcrumb>
          <Breadcrumb.Item href="/">General</Breadcrumb.Item>
          <Breadcrumb.Item active>Customer Schedule</Breadcrumb.Item>
        </Breadcrumb>
        <div className="col-md-12 text-primary">
          <h4>Customer Schedule</h4>
        </div>
      </div>

      <div className="app-main-content-list-container list-page customer-schedule-page">
        <div className="customer-schedule-toolbar mb-3">
          <div className="customer-schedule-week-nav">
            <button
              type="button"
              className="btn btn-default btn-sm"
              onClick={() => shiftWeek(-1)}
              aria-label="Previous week"
            >
              <ChevronLeft size={16} />
            </button>
            <div className="customer-schedule-datepicker">
              <DatePicker
                selected={weekAnchor}
                onChange={onPickDate}
                dateFormat="MMM d, yyyy"
                showWeekNumbers
                calendarStartDay={1}
                className="form-control form-control-sm"
                popperPlacement="bottom-start"
                popperClassName="customer-schedule-datepicker-popper"
                popperProps={{ strategy: "fixed" }}
              />
            </div>
            <button
              type="button"
              className="btn btn-default btn-sm"
              onClick={() => shiftWeek(1)}
              aria-label="Next week"
            >
              <ChevronRight size={16} />
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm customer-schedule-this-week-btn"
              onClick={() => setWeekAnchor(getWeekStart(new Date()).toDate())}
            >
              This Week
            </button>
            <div className="customer-schedule-search">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search customer name..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                aria-label="Search customer name"
              />
            </div>
          </div>
          <div className="customer-schedule-week-label text-muted">{weekRangeLabel}</div>
        </div>

        {loading ? (
          <div className="d-flex align-items-center gap-2 p-4 text-muted">
            <Spinner animation="border" size="sm" role="status" aria-label="Loading" />
            <span>Loading customers…</span>
          </div>
        ) : (
          <div className="sticky-table-scroll customer-schedule-table-wrap">
            <table className="personnel-info-table personnel-info-table--freeze-leading customer-schedule-table">
              <thead>
                <tr>
                  <th className="th-index">No.</th>
                  <th className="th-freeze-customer-name">Customer</th>
                  {weekDays.map((day) => (
                    <th key={day.key} className="customer-schedule-day-th">
                      <div>{day.label}</div>
                      <div className="customer-schedule-day-date">{day.dateLabel}</div>
                    </th>
                  ))}
                  <th className="customer-schedule-week-leave-th">Week Leave</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.length === 0 ? (
                  <tr>
                    <td colSpan={weekDays.length + 3} className="text-muted">
                      {searchText.trim()
                        ? "No customers match your search."
                        : "No active customers found."}
                    </td>
                  </tr>
                ) : (
                  filteredCustomers.map((customer, index) => {
                    const weekLeaveItems = getWeekLeaveItems(customer);
                    return (
                      <tr key={customer.id || index}>
                        <td className="td-index">{index + 1}</td>
                        <td className="td-freeze-customer-name">
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 customer-schedule-name-btn"
                            onClick={() => openEditModal(customer)}
                          >
                            {formatCustomerDisplayName(customer)}
                          </button>
                        </td>
                        {weekDays.map((day) => (
                          <td key={day.key} className="customer-schedule-day-cell">
                            {renderDayStatusCell(customer, day)}
                          </td>
                        ))}
                        <td className="customer-schedule-week-leave-cell">
                          {weekLeaveItems.length === 0 ? (
                            <span className="customer-schedule-week-leave-none">None</span>
                          ) : (
                            <div className="customer-schedule-week-leave-list">
                              {weekLeaveItems.map((item) => (
                                <span
                                  key={item.id || formatWeekLeaveRange(item)}
                                  className="customer-schedule-week-leave-pill"
                                  title={formatWeekLeaveRange(item)}
                                >
                                  {formatWeekLeaveRange(item)}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        show={showEditModal}
        onHide={closeEditModal}
        centered
        className="customer-schedule-edit-modal"
      >
        <Modal.Body>
          <div className="customer-schedule-edit-header">
            <div>
              <h5 className="customer-schedule-edit-title">Edit Schedule</h5>
              <p className="customer-schedule-edit-subtext">
                Set default days and leave dates for{" "}
                <strong>{formatCustomerDisplayName(editingCustomer)}</strong>. Default days are
                saved on the customer profile.
              </p>
            </div>
            <button
              type="button"
              className="btn btn-link customer-schedule-edit-close"
              onClick={closeEditModal}
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          <div className="customer-schedule-default-section">
            <div className="customer-schedule-section-title-row">
              <span className="customer-schedule-section-title">Default</span>
              <span className="customer-schedule-section-meta">Uses customer days of week</span>
            </div>
            <div className="customer-schedule-day-grid">
              {WEEKDAY_KEYS.map((dayKey) => {
                const checked = selectedDays.includes(dayKey);
                return (
                  <label
                    key={dayKey}
                    className={`customer-schedule-day-card${checked ? " is-checked" : ""}`}
                  >
                    <span>{WEEKDAY_FULL_LABELS[dayKey]}</span>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={!canEdit}
                      onChange={() => toggleDefaultDay(dayKey)}
                    />
                  </label>
                );
              })}
            </div>
          </div>

          <div className="customer-schedule-leave-section">
            <div className="customer-schedule-leave-block">
              <div className="customer-schedule-leave-header">
                <div>
                  <div className="customer-schedule-section-title">Upcoming Leave</div>
                  <div className="customer-schedule-section-meta">
                    Leave overrides the daily schedule.
                  </div>
                </div>
                {canEdit && (
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={openAddLeaveModal}
                    disabled={!!deletingLeaveId || savingLeave || saving}
                  >
                    <Plus size={14} className="me-1" />
                    Add Leave
                  </button>
                )}
              </div>
              {renderLeaveList(upcomingLeave, "No upcoming leave.", { allowDelete: true })}
            </div>

            <div className="customer-schedule-leave-block">
              <div className="customer-schedule-leave-header">
                <div>
                  <div className="customer-schedule-section-title">Past Leave</div>
                  <div className="customer-schedule-section-meta">
                    Saved for history. Read-only here.
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => setShowPastLeave((v) => !v)}
                >
                  {showPastLeave ? "Hide" : `Show (${pastLeave.length})`}
                </button>
              </div>
              {showPastLeave && renderLeaveList(pastLeave, "No past leave.")}
            </div>
          </div>

          <div className="customer-schedule-edit-actions">
            <Button
              variant="default"
              size="sm"
              onClick={closeEditModal}
              disabled={saving || savingLeave || !!deletingLeaveId}
            >
              Cancel
            </Button>
            {canEdit && (
              <Button
                variant="primary"
                size="sm"
                onClick={saveDefaultSchedule}
                disabled={saving || savingLeave || !!deletingLeaveId}
              >
                {saving ? "Saving…" : "Save"}
              </Button>
            )}
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={showAddLeaveModal}
        onHide={closeAddLeaveModal}
        centered
        className="customer-schedule-add-leave-modal"
      >
        <Modal.Header closeButton={!savingLeave}>
          <Modal.Title>Add Leave</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="customer-schedule-leave-form">
            <div className="customer-schedule-leave-field">
              <label className="field-label">Start Date</label>
              <DatePicker
                selected={leaveStartDate}
                onChange={(date) => {
                  setLeaveStartDate(date);
                  if (date && leaveEndDate && moment(leaveEndDate).isBefore(moment(date), "day")) {
                    setLeaveEndDate(date);
                  }
                }}
                dateFormat="MMM d, yyyy"
                placeholderText="Select start date"
                className="form-control form-control-sm"
              />
            </div>
            <div className="customer-schedule-leave-field">
              <label className="field-label">End Date</label>
              <DatePicker
                selected={leaveEndDate}
                onChange={(date) => setLeaveEndDate(date)}
                minDate={leaveStartDate || undefined}
                dateFormat="MMM d, yyyy"
                placeholderText="Select end date"
                className="form-control form-control-sm"
              />
            </div>
            <div className="customer-schedule-leave-field">
              <label className="field-label">Note</label>
              <textarea
                className="form-control form-control-sm"
                rows={3}
                value={leaveNote}
                onChange={(e) => setLeaveNote(e.target.value)}
                placeholder="Optional note"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="default" size="sm" onClick={closeAddLeaveModal} disabled={savingLeave}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={saveLeave} disabled={savingLeave}>
            {savingLeave ? "Saving…" : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CustomerSchedule;
